/* eslint-disable @typescript-eslint/naming-convention, jsdoc/require-param, jsdoc/require-returns */
import { cleanFBXName, findDocumentNode, getPropertyValue, getSafeFBXObjectId } from "../types/fbxTypes";
/**
 * Build a connection graph from a parsed FBX document.
 * Maps object IDs to their FBXNode and resolves parent-child relationships.
 */
export function resolveConnections(doc) {
    const objects = new Map();
    const objectEntries = [];
    const childrenOf = new Map();
    const parentOf = new Map();
    const connections = [];
    const connectionEntries = [];
    const diagnostics = [];
    // Legacy (6.x and older) objects are identified by their full "Class::Name" string, exactly as the SDK does,
    // so that a material, texture and video sharing a base name stay distinct. Connections that only carry the
    // bare name fall back to the first object with that name.
    const legacyIds = new Map();
    const legacyIdsByCleanName = new Map();
    const syntheticLegacyIds = new Map();
    const legacyGeometryByModelId = new Map();
    // Every numeric id the file uses is reserved up front so that synthetic ids (legacy objects, inline shapes)
    // and the stand-ins for ids beyond 2^53 never collide with a real object.
    const objectsNode = findDocumentNode(doc, "Objects");
    const connectionsNode = findDocumentNode(doc, "Connections");
    const usedIds = new Set();
    const reserve = (property) => {
        const value = property?.value;
        if (typeof value === "number" && Number.isSafeInteger(value)) {
            usedIds.add(value);
        }
    };
    for (const obj of objectsNode?.children ?? []) {
        reserve(obj.properties[0]);
    }
    for (const c of connectionsNode?.children ?? []) {
        reserve(c.properties[1]);
        reserve(c.properties[2]);
        reserve(c.properties[3]);
    }
    let nextSyntheticId = -1;
    const allocateId = () => {
        while (usedIds.has(nextSyntheticId)) {
            nextSyntheticId--;
        }
        const id = nextSyntheticId--;
        usedIds.add(id);
        return id;
    };
    // Ids whose magnitude exceeds 2^53 are keyed by their exact decimal text; distinct texts get distinct ids.
    const remappedIds = new Map();
    const numericIdOf = (property) => {
        if (!property) {
            return undefined;
        }
        if (property.raw !== undefined) {
            let id = remappedIds.get(property.raw);
            if (id === undefined) {
                id = allocateId();
                remappedIds.set(property.raw, id);
            }
            return id;
        }
        return getSafeFBXObjectId(property.value);
    };
    const toObjectId = (property) => {
        const numericId = numericIdOf(property);
        if (numericId !== undefined) {
            return numericId;
        }
        const value = property?.value;
        if (typeof value !== "string") {
            return undefined;
        }
        const direct = legacyIds.get(value);
        if (direct !== undefined) {
            return direct;
        }
        const legacyName = cleanFBXName(value);
        if (legacyName === "Scene") {
            return 0;
        }
        return legacyIdsByCleanName.get(legacyName);
    };
    const getLegacyId = (rawName) => {
        let id = legacyIds.get(rawName);
        if (id === undefined) {
            id = allocateId();
            legacyIds.set(rawName, id);
            const clean = cleanFBXName(rawName);
            if (!legacyIdsByCleanName.has(clean)) {
                legacyIdsByCleanName.set(clean, id);
            }
        }
        return id;
    };
    const getSyntheticLegacyId = (role, name) => {
        let idsByName = syntheticLegacyIds.get(role);
        if (!idsByName) {
            idsByName = new Map();
            syntheticLegacyIds.set(role, idsByName);
        }
        let id = idsByName.get(name);
        if (id === undefined) {
            id = allocateId();
            idsByName.set(name, id);
        }
        return id;
    };
    /** Turns `Shape` children of a geometry into a BlendShape deformer with one channel and shape geometry each. */
    const synthesizeInlineShapes = (owner, geometryId, key) => {
        const shapes = owner.children.filter((c) => c.name === "Shape");
        if (shapes.length === 0) {
            return;
        }
        const deformerId = getSyntheticLegacyId("BlendShape", key);
        const deformer = createLegacyObject("Deformer", deformerId, `Deformer::${cleanFBXName(key)}`, "BlendShape", [createVersionNode(100)]);
        objects.set(deformerId, deformer);
        objectEntries.push({ id: deformerId, node: deformer, source: "legacySyntheticBlendShape", legacyName: key, synthetic: true });
        addConnection(connections, childrenOf, parentOf, diagnostics, "OO", deformerId, geometryId);
        for (const shape of shapes) {
            const shapeName = cleanFBXName(getPropertyValue(shape, 0) ?? "Shape");
            const channelId = getSyntheticLegacyId("BlendShapeChannel", `${key}|${shapeName}`);
            const channel = createLegacyObject("Deformer", channelId, `SubDeformer::${shapeName}`, "BlendShapeChannel", [
                createVersionNode(100),
                { name: "DeformPercent", properties: [{ type: "float64", value: 0 }], children: [] },
                {
                    name: "Properties60",
                    properties: [],
                    children: [
                        {
                            name: "Property",
                            properties: [
                                { type: "string", value: "DeformPercent" },
                                { type: "string", value: "Number" },
                                { type: "string", value: "A" },
                                { type: "float64", value: 0 },
                            ],
                            children: [],
                        },
                    ],
                },
            ]);
            objects.set(channelId, channel);
            objectEntries.push({ id: channelId, node: channel, source: "legacySyntheticBlendShape", legacyName: `${key}|${shapeName}`, synthetic: true });
            addConnection(connections, childrenOf, parentOf, diagnostics, "OO", channelId, deformerId);
            const shapeId = getSyntheticLegacyId("Shape", `${key}|${shapeName}`);
            const shapeGeometry = createLegacyObject("Geometry", shapeId, `Geometry::${shapeName}`, "Shape", shape.children);
            objects.set(shapeId, shapeGeometry);
            objectEntries.push({ id: shapeId, node: shapeGeometry, source: "legacySyntheticBlendShape", legacyName: `${key}|${shapeName}`, synthetic: true });
            addConnection(connections, childrenOf, parentOf, diagnostics, "OO", shapeId, channelId);
        }
    };
    // Build object map from Objects section
    if (objectsNode) {
        for (const obj of objectsNode.children) {
            const idProp = obj.properties[0];
            if (idProp) {
                const id = numericIdOf(idProp);
                if (id !== undefined) {
                    objects.set(id, obj);
                    objectEntries.push({ id, node: obj, source: "Objects", synthetic: false });
                    if (obj.name === "Geometry" && getPropertyValue(obj, 2) === "Mesh") {
                        // FBX 7.1 stores blend shapes inline in the geometry, like 6.x files.
                        synthesizeInlineShapes(obj, id, String(id));
                    }
                }
                else if (typeof idProp.value === "string") {
                    const legacyName = idProp.value;
                    const id = getLegacyId(legacyName);
                    const normalized = normalizeLegacyObject(obj, id);
                    objects.set(id, normalized);
                    objectEntries.push({ id, node: normalized, source: "Objects", legacyName, synthetic: false });
                    const legacySubType = obj.name === "Model" ? getPropertyValue(obj, 1) : undefined;
                    if (legacySubType === "Mesh") {
                        const geometryId = getSyntheticLegacyId("Geometry", legacyName);
                        const geometry = createLegacyGeometry(obj, geometryId);
                        objects.set(geometryId, geometry);
                        objectEntries.push({ id: geometryId, node: geometry, source: "legacySyntheticGeometry", legacyName, synthetic: true });
                        addConnection(connections, childrenOf, parentOf, diagnostics, "OO", geometryId, id);
                        legacyGeometryByModelId.set(id, geometryId);
                        // Inline blend shapes become a BlendShape deformer with one channel and one shape geometry each.
                        synthesizeInlineShapes(obj, geometryId, legacyName);
                    }
                    else if (legacySubType === "Light" || legacySubType === "Camera") {
                        // 6.x lights and cameras keep their attribute properties on the model; expose them as a NodeAttribute.
                        const attributeId = getSyntheticLegacyId("NodeAttribute", legacyName);
                        const attribute = createLegacyObject("NodeAttribute", attributeId, `NodeAttribute::${cleanFBXName(legacyName)}`, legacySubType, obj.children);
                        objects.set(attributeId, attribute);
                        objectEntries.push({ id: attributeId, node: attribute, source: "legacySyntheticAttribute", legacyName, synthetic: true });
                        addConnection(connections, childrenOf, parentOf, diagnostics, "OO", attributeId, id);
                    }
                }
            }
        }
    }
    // Parse connections
    if (connectionsNode) {
        for (const c of connectionsNode.children) {
            if (c.name !== "C" && c.name !== "Connect") {
                continue;
            }
            const connectionIndex = connectionEntries.length;
            let type = getPropertyValue(c, 0);
            let childIdRaw = c.properties[1];
            let parentIdRaw = c.properties[2];
            let poPropertyName;
            // "PO" connects a property of the first object to the second object (6.x constraints use it for
            // "Constrained Object"); it is the mirror image of "OP", so store it as OP from the object to the owner.
            if (type === "PO" && typeof c.properties[2]?.value === "string" && c.properties.length > 3) {
                type = "OP";
                poPropertyName = c.properties[2].value;
                childIdRaw = c.properties[3];
                parentIdRaw = c.properties[1];
            }
            const entry = {
                source: c.name,
                rawType: type,
                accepted: false,
            };
            connectionEntries.push(entry);
            if (type !== "OO" && type !== "OP") {
                const childId = toObjectId(childIdRaw);
                const parentId = toObjectId(parentIdRaw);
                diagnostics.push({
                    reason: "unsupported-connection-type",
                    message: `Unsupported FBX connection type '${type ?? ""}' was not added to the graph.`,
                    connectionIndex,
                    type,
                    childId,
                    parentId,
                });
                continue;
            }
            if (childIdRaw === undefined || parentIdRaw === undefined) {
                diagnostics.push({
                    reason: "missing-connection-endpoint",
                    message: "FBX connection is missing a child or parent endpoint.",
                    connectionIndex,
                    type,
                });
                continue;
            }
            const childId = toObjectId(childIdRaw);
            let parentId = toObjectId(parentIdRaw);
            // 6.x deformers connect to the model; the importer expects them on the geometry.
            if (childId !== undefined && parentId !== undefined && objects.get(childId)?.name === "Deformer") {
                parentId = legacyGeometryByModelId.get(parentId) ?? parentId;
            }
            if (childId === undefined || parentId === undefined) {
                diagnostics.push({
                    reason: "unresolved-legacy-endpoint",
                    message: "FBX connection references a legacy string endpoint that is not present in the object table.",
                    connectionIndex,
                    type,
                });
                continue;
            }
            const propertyName = poPropertyName ?? (type === "OP" && c.properties.length > 3 ? getPropertyValue(c, 3) : undefined);
            entry.childId = childId;
            entry.parentId = parentId;
            entry.propertyName = propertyName;
            if (childId === parentId) {
                diagnostics.push({
                    reason: "self-loop",
                    message: "FBX connection references the same object as child and parent.",
                    connectionIndex,
                    type,
                    childId,
                    parentId,
                    propertyName,
                });
            }
            if (!objects.has(childId)) {
                diagnostics.push({
                    reason: "unresolved-object-reference",
                    message: "FBX connection child ID is not present in the object table.",
                    connectionIndex,
                    type,
                    childId,
                    parentId,
                    propertyName,
                });
            }
            if (parentId !== 0 && !objects.has(parentId)) {
                diagnostics.push({
                    reason: "unresolved-object-reference",
                    message: "FBX connection parent ID is not present in the object table.",
                    connectionIndex,
                    type,
                    childId,
                    parentId,
                    propertyName,
                });
            }
            addConnection(connections, childrenOf, parentOf, diagnostics, type, childId, parentId, propertyName, connectionIndex);
            entry.accepted = true;
        }
    }
    return { objects, objectEntries, childrenOf, parentOf, connections, connectionEntries, diagnostics };
}
/** Get all child objects of a given parent ID, optionally filtered by node name */
export function getChildren(map, parentId, nodeName) {
    const children = map.childrenOf.get(parentId) ?? [];
    const result = [];
    for (const child of children) {
        const node = map.objects.get(child.id);
        if (node && (!nodeName || node.name === nodeName)) {
            result.push({ id: child.id, node, propertyName: child.propertyName });
        }
    }
    return result;
}
function createVersionNode(version) {
    return { name: "Version", properties: [{ type: "int32", value: version }], children: [] };
}
function createLegacyObject(nodeName, id, name, subType, children) {
    return {
        name: nodeName,
        properties: [
            { type: "int64", value: id },
            { type: "string", value: name },
            { type: "string", value: subType },
        ],
        children,
    };
}
function addConnection(connections, childrenOf, parentOf, diagnostics, type, childId, parentId, propertyName, connectionIndex) {
    connections.push({ type, childId, parentId, propertyName });
    if (!childrenOf.has(parentId)) {
        childrenOf.set(parentId, []);
    }
    childrenOf.get(parentId).push({ id: childId, propertyName });
    const existingParent = parentOf.get(childId);
    if (existingParent) {
        diagnostics.push({
            reason: "duplicate-parent",
            message: "FBX object has multiple parents; preserving the existing last-parent behavior.",
            connectionIndex,
            type,
            childId,
            parentId,
            propertyName,
        });
    }
    parentOf.set(childId, { id: parentId, propertyName });
}
function normalizeLegacyObject(node, id) {
    const name = cleanFBXName(getPropertyValue(node, 0) ?? node.name);
    const subType = getPropertyValue(node, 1) ?? "";
    return {
        ...node,
        properties: [
            { type: "int64", value: id },
            { type: "string", value: name },
            { type: "string", value: subType },
        ],
    };
}
function createLegacyGeometry(modelNode, geometryId) {
    const name = cleanFBXName(getPropertyValue(modelNode, 0) ?? "Geometry");
    return {
        name: "Geometry",
        properties: [
            { type: "int64", value: geometryId },
            { type: "string", value: name },
            { type: "string", value: "Mesh" },
        ],
        children: modelNode.children,
    };
}
//# sourceMappingURL=connections.js.map